import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';
import {
	ADMIN_SYMBOL_MATRIX_SEARCH_MAX_LENGTH,
	AdminSymbolPageSize,
	IAdminSymbolDataset,
	isAdminSymbolPageSize,
	useAdminSymbolDatasetsQuery,
} from '../../api/admin';
import { AppCard } from '../../components';
import {
	AdminDatasetSelector,
	MatrixDatasetLayouts,
	SymbolMatrixTable,
} from '../../components/Admin';
import { parsePositiveIdList } from '../../utils/searchParams';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE: AdminSymbolPageSize = 100;
const DATASETS_PARAM = 'matrix_datasets';
const PAGE_PARAM = 'matrix_page';
const PAGE_SIZE_PARAM = 'matrix_page_size';
const COMPANY_PARAM = 'matrix_company';
const EMPTY_DATASETS: IAdminSymbolDataset[] = [];

function parsePositiveInteger(value: string | null, fallback: number) {
	if (!value || !/^\d+$/.test(value)) return fallback;
	const parsed = Number(value);
	return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function parsePageSize(value: string | null): AdminSymbolPageSize {
	const parsed = Number(value);
	return isAdminSymbolPageSize(parsed) ? parsed : DEFAULT_PAGE_SIZE;
}

export default function SymbolMatrixTab() {
	const { data: datasets, isLoading: datasetsLoading, isError: datasetsError } = useAdminSymbolDatasetsQuery();
	const [searchParams, setSearchParams] = useSearchParams();

	const urlDatasetIds = React.useMemo(
		() => parsePositiveIdList(searchParams.get(DATASETS_PARAM)),
		[searchParams],
	);
	const urlPage = parsePositiveInteger(searchParams.get(PAGE_PARAM), DEFAULT_PAGE);
	const pageSize = parsePageSize(searchParams.get(PAGE_SIZE_PARAM));
	const companySearch = (searchParams.get(COMPANY_PARAM) ?? '')
		.trim()
		.slice(0, ADMIN_SYMBOL_MATRIX_SEARCH_MAX_LENGTH);

	const datasetById = React.useMemo(
		() => new Map((datasets ?? EMPTY_DATASETS).map((dataset) => [dataset.id, dataset])),
		[datasets],
	);
	const selectedDatasets = React.useMemo(
		() => urlDatasetIds.flatMap((id) => {
			const dataset = datasetById.get(id);
			return dataset ? [dataset] : [];
		}),
		[datasetById, urlDatasetIds],
	);

	const datasetsHaveLoaded = datasets !== undefined;
	const selectedDatasetIds = React.useMemo(
		() => (datasetsHaveLoaded
			? selectedDatasets.map((dataset) => dataset.id)
			: urlDatasetIds),
		[datasetsHaveLoaded, selectedDatasets, urlDatasetIds],
	);
	const selectionWasSanitized = datasetsHaveLoaded
		&& selectedDatasetIds.length !== urlDatasetIds.length;
	const page = selectionWasSanitized ? DEFAULT_PAGE : urlPage;
	const serializedDatasetIds = selectedDatasetIds.join(',');

	React.useEffect(() => {
		const nextParams = new URLSearchParams(searchParams);
		let changed = false;

		if (serializedDatasetIds) {
			if (nextParams.get(DATASETS_PARAM) !== serializedDatasetIds) {
				nextParams.set(DATASETS_PARAM, serializedDatasetIds);
				changed = true;
			}
		} else if (nextParams.has(DATASETS_PARAM)) {
			nextParams.delete(DATASETS_PARAM);
			changed = true;
		}

		if (page === DEFAULT_PAGE) {
			if (nextParams.has(PAGE_PARAM)) {
				nextParams.delete(PAGE_PARAM);
				changed = true;
			}
		} else if (nextParams.get(PAGE_PARAM) !== String(page)) {
			nextParams.set(PAGE_PARAM, String(page));
			changed = true;
		}
		if (pageSize === DEFAULT_PAGE_SIZE) {
			if (nextParams.has(PAGE_SIZE_PARAM)) {
				nextParams.delete(PAGE_SIZE_PARAM);
				changed = true;
			}
		} else if (nextParams.get(PAGE_SIZE_PARAM) !== String(pageSize)) {
			nextParams.set(PAGE_SIZE_PARAM, String(pageSize));
			changed = true;
		}
		if (companySearch) {
			if (nextParams.get(COMPANY_PARAM) !== companySearch) {
				nextParams.set(COMPANY_PARAM, companySearch);
				changed = true;
			}
		} else if (nextParams.has(COMPANY_PARAM)) {
			nextParams.delete(COMPANY_PARAM);
			changed = true;
		}

		if (changed) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [companySearch, page, pageSize, searchParams, serializedDatasetIds, setSearchParams]);

	const writeUrlState = React.useCallback((
		nextDatasetIds: number[],
		nextPage: number,
		nextPageSize: AdminSymbolPageSize,
		nextCompanySearch: string = companySearch,
	) => {
		const nextParams = new URLSearchParams(searchParams);
		if (nextDatasetIds.length > 0) {
			nextParams.set(DATASETS_PARAM, nextDatasetIds.join(','));
		} else {
			nextParams.delete(DATASETS_PARAM);
		}
		if (nextPage === DEFAULT_PAGE) {
			nextParams.delete(PAGE_PARAM);
		} else {
			nextParams.set(PAGE_PARAM, String(nextPage));
		}
		if (nextPageSize === DEFAULT_PAGE_SIZE) {
			nextParams.delete(PAGE_SIZE_PARAM);
		} else {
			nextParams.set(PAGE_SIZE_PARAM, String(nextPageSize));
		}
		const normalizedCompanySearch = nextCompanySearch
			.trim()
			.slice(0, ADMIN_SYMBOL_MATRIX_SEARCH_MAX_LENGTH);
		if (normalizedCompanySearch) {
			nextParams.set(COMPANY_PARAM, normalizedCompanySearch);
		} else {
			nextParams.delete(COMPANY_PARAM);
		}
		setSearchParams(nextParams, { replace: true });
	}, [companySearch, searchParams, setSearchParams]);

	const handleDatasetChange = React.useCallback((
		nextDatasets: IAdminSymbolDataset[],
	) => {
		const seen = new Set<number>();
		const orderedIds = nextDatasets.reduce<number[]>((ids, dataset) => {
			if (!seen.has(dataset.id)) {
				seen.add(dataset.id);
				ids.push(dataset.id);
			}
			return ids;
		}, []);
		writeUrlState(orderedIds, DEFAULT_PAGE, pageSize);
	}, [pageSize, writeUrlState]);

	const handlePageChange = React.useCallback((nextPage: number) => {
		writeUrlState(selectedDatasetIds, nextPage, pageSize);
	}, [pageSize, selectedDatasetIds, writeUrlState]);

	const handlePageSizeChange = React.useCallback((nextPageSize: AdminSymbolPageSize) => {
		writeUrlState(selectedDatasetIds, DEFAULT_PAGE, nextPageSize);
	}, [selectedDatasetIds, writeUrlState]);

	const handleCompanySearchChange = React.useCallback((value: string) => {
		writeUrlState(selectedDatasetIds, DEFAULT_PAGE, pageSize, value);
	}, [pageSize, selectedDatasetIds, writeUrlState]);

	const handleLoadSavedSelection = React.useCallback((datasetIds: number[]) => {
		writeUrlState(datasetIds, DEFAULT_PAGE, pageSize);
	}, [pageSize, writeUrlState]);

	return (
		<AppCard>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: {
						xs: 'minmax(0, 1fr)',
						lg: 'minmax(420px, 1fr) minmax(620px, 1.5fr)',
					},
					gap: 2,
					alignItems: 'start',
					mb: 3,
				}}
			>
				<AdminDatasetSelector
					datasets={datasets ?? EMPTY_DATASETS}
					loading={datasetsLoading}
					error={datasetsError}
					selectedDatasets={selectedDatasets}
					helperText="Selection order defines the matrix column order."
					onChange={handleDatasetChange}
				/>
				<MatrixDatasetLayouts
					selectedDatasetIds={selectedDatasetIds}
					onLoad={handleLoadSavedSelection}
				/>
			</Box>

			{datasetsLoading && urlDatasetIds.length > 0 ? (
				<Stack minHeight={400} alignItems="center" justifyContent="center" spacing={1}>
					<CircularProgress size={24} />
					<Typography variant="body2" color="text.secondary">
						Restoring the selected datasets…
					</Typography>
				</Stack>
			) : (
				<SymbolMatrixTable
					selectedDatasets={selectedDatasets}
					companySearch={companySearch}
					onCompanySearchChange={handleCompanySearchChange}
					page={page}
					pageSize={pageSize}
					onPageChange={handlePageChange}
					onPageSizeChange={handlePageSizeChange}
				/>
			)}
		</AppCard>
	);
}
